// Angular Files
import { Component, Inject, OnDestroy } from '@angular/core';

// Angular Material Files
import { MatLegacyDialogRef as MatDialogRef, MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/legacy-dialog';

// Other External Files
import { Subject, forkJoin, of } from 'rxjs';
import { takeUntil, switchMap, map, catchError } from 'rxjs/operators';

// Teller Online Files
import {
    ConfigurationService,
    ConfigurationImportResult,
    ChangeType,
    PublicPortalSiteExportDto,
    TellerConnectionExportDto,
    PageExportDto,
    MenuItemExportDto,
    EmailTemplateExportDto,
    SMSTemplateExportDto,
    SearchTypeExportDto,
    PageItemTypeExportDto,
    InboundRedirectSourceExportDto,
    ImportAssetInfo,
    AssetChangeInfo as ServiceAssetChangeInfo,
    SearchTypeImageChange,
    PageItemTypeImageChange,
    AssetChangeType,
    TellerConnectionModel,
    PageModel,
    MenuItemModel,
    EmailTemplateModel,
    SmsTemplateModel,
    PublicPortalSiteModel,
    SearchTypeModel,
    PageItemTypeModel,
    InboundRedirectSourceModel
} from 'apps/admin-portal/src/app/core/services';

export interface ConfigImportWizardData {
    siteId: number;
    tenantName: string;
}

type WizardStep = 'file-select' | 'preview' | 'importing' | 'complete' | 'error';

/** Represents a single property change */
interface PropertyChange {
    name: string;
    currentValue: string;
    incomingValue: string;
}

/** Represents an entity with its computed change type for display */
interface EntityWithChange<T> {
    data: T;
    changeType: ChangeType;
    propertyChanges?: PropertyChange[];
}

/** SearchType with computed change info for display */
interface SearchTypeWithChanges extends EntityWithChange<SearchTypeExportDto> {
    propertyChanges: PropertyChange[];
    instructionImage?: ServiceAssetChangeInfo;
}

/** PageItemType with computed change info for display */
interface PageItemTypeWithChanges extends EntityWithChange<PageItemTypeExportDto> {
    propertyChanges: PropertyChange[];
    image?: ServiceAssetChangeInfo;
}

/** InboundRedirectSource with computed change info for display */
interface InboundRedirectSourceWithChanges extends EntityWithChange<InboundRedirectSourceExportDto> {
    propertyChanges: PropertyChange[];
}

/** Page with computed change info for display */
interface PageWithChanges extends EntityWithChange<PageExportDto> {
    propertyChanges: PropertyChange[];
    searchTypes: SearchTypeWithChanges[];
    pageItemTypes: PageItemTypeWithChanges[];
    inboundRedirectSources: InboundRedirectSourceWithChanges[];
}

/** Teller connection with computed change info for display */
interface TellerConnectionWithChanges {
    data: TellerConnectionExportDto;
    changeType: ChangeType;
    propertyChanges: PropertyChange[];
    pages: PageWithChanges[];
}

/** Computed preview data for display */
interface ComputedPreview {
    sourceTenantName: string;
    targetTenantName: string;
    sourceEnvironmentName: string | null;
    targetEnvironmentName: string | null;
    siteChanges: SitePropertyChange[];
    tellerConnections: TellerConnectionWithChanges[];
    menuItems: EntityWithChange<MenuItemExportDto>[];
    emailTemplates: EntityWithChange<EmailTemplateExportDto>[];
    smsTemplates: EntityWithChange<SMSTemplateExportDto>[];
    assets: ImportAssetInfo | null;
    assetChanges: AssetChangeInfo[];
    searchTypeImageChanges: AssetChangeInfo[];
}

interface SitePropertyChange {
    name: string;
    currentValue: string;
    incomingValue: string;
    hasChanged: boolean;
}

/** Represents a computed asset change for display */
interface AssetChangeInfo {
    name: string;
    hasIncoming: boolean;
    currentExists: boolean;
    changeType: 'new' | 'replace' | 'unchanged' | 'none';
}

@Component({
    selector: 'app-config-import-wizard',
    templateUrl: './config-import-wizard.component.html',
    styleUrls: ['./config-import-wizard.component.scss'],
    host: {
        class: 'config-import-wizard-dialog'
    }
})
export class ConfigImportWizardComponent implements OnDestroy {
    // Fields present on ExportDtos that must NOT be compared
    private static readonly EXCLUDE_KEYS = new Set([
        'configNo',                // Identity key on all entities, used for matching
        'name',                    // Template matching key
        'pageConfigNo',            // Relational reference on MenuItem
        'instructionsImageAssetPath', // SearchType asset path (handled by image change detection)
        'imageAssetPath',          // PageItemType asset path (handled by image change detection)
        'tellerSessionDateFormat', // Intentionally excluded: Legacy-only, not written by UpdateFromExportDto
        'sessionNamePrefix',       // Same as above
        'tellerSystemNo',          // Import-only Teller system resolver; not stored on entity
    ]);

    // Fields whose values are too large to display — shown as (content)/(modified) instead
    private static readonly CONTENT_KEYS = new Set(['bodyHTML', 'bodyTemplate']);

    public currentStep: WizardStep = 'file-select';
    public selectedFile: File | null = null;
    public isLoading: boolean = false;
    public previewResult: ConfigurationImportResult | null = null;
    public importResult: ConfigurationImportResult | null = null;
    public errorMessage: string | null = null;

    // Current configuration data for comparison
    private _currentSite: PublicPortalSiteModel | null = null;
    private _currentTellerConnections: TellerConnectionModel[] = [];
    private _currentPages: PageModel[] = [];
    private _currentMenuItems: MenuItemModel[] = [];
    private _currentEmailTemplates: EmailTemplateModel[] = [];
    private _currentSmsTemplates: SmsTemplateModel[] = [];

    // Child entity storage (keyed by page ID)
    private _currentSearchTypes: Map<number, SearchTypeModel[]> = new Map();
    private _currentPageItemTypes: Map<number, PageItemTypeModel[]> = new Map();
    private _currentInboundRedirectSources: Map<number, InboundRedirectSourceModel[]> = new Map();

    // Search type instruction images from incoming config
    private _incomingSearchTypeImageChanges: SearchTypeImageChange[] = [];

    // Page item type images from incoming config
    private _incomingPageItemTypeImageChanges: PageItemTypeImageChange[] = [];

    // Computed preview after comparison
    public computedPreview: ComputedPreview | null = null;

    // For template access
    public ChangeType = ChangeType;
    public AssetChangeType = AssetChangeType;

    private _destroy$ = new Subject<void>();

    constructor(
        private configurationService: ConfigurationService,
        public dialogRef: MatDialogRef<ConfigImportWizardComponent>,
        @Inject(MAT_DIALOG_DATA) public data: ConfigImportWizardData
    ) {}

    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }

    public onFileSelected(event: Event) {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files[0]) {
            const file = input.files[0];
            if (file.name.endsWith('.zip')) {
                this.selectedFile = file;
                this.errorMessage = null;
            } else {
                this.selectedFile = null;
                this.errorMessage = 'Please select a ZIP file';
            }
        }
    }

    public onPreview() {
        if (!this.selectedFile) return;

        this.isLoading = true;
        this.errorMessage = null;

        // Load current configuration and preview in parallel
        forkJoin({
            preview: this.configurationService.previewImport(this.data.siteId, this.selectedFile),
            site: this.configurationService.getSite(true),
            tellerConnections: this.configurationService.getTellerConnections(),
            pages: this.configurationService.getPages(),
            menuItems: this.configurationService.getMenuItems(),
            emailTemplates: this.configurationService.getEmailTemplates(),
            smsTemplates: this.configurationService.getSmsTemplates()
        }).pipe(
            switchMap(initialResult => {
                // Build parallel requests for all child entities
                const searchTypeRequests: {[key: string]: any} = {};
                const pageItemTypeRequests: {[key: string]: any} = {};
                const inboundRedirectRequests: {[key: string]: any} = {};

                initialResult.pages.forEach(page => {
                    if (page.id) {
                        searchTypeRequests[`page_${page.id}`] = this.configurationService.getSearchTypes(page.id);
                        pageItemTypeRequests[`page_${page.id}`] = this.configurationService.getPageItemTypes(page.id);
                        inboundRedirectRequests[`page_${page.id}`] = this.configurationService.getInboundRedirectSources(page.id);
                    }
                });

                // Wrap each request individually so one failure doesn't zero out the entire batch
                const wrapRequests = (requests: {[key: string]: any}) => {
                    if (!Object.keys(requests).length) return of({});
                    const wrapped = Object.fromEntries(
                        Object.entries(requests).map(([key, obs]) => [
                            key,
                            obs.pipe(catchError(() => of([])))
                        ])
                    );
                    return forkJoin(wrapped);
                };

                // Return combined result with child entities
                return forkJoin({
                    initial: of(initialResult),
                    searchTypes: wrapRequests(searchTypeRequests),
                    pageItemTypes: wrapRequests(pageItemTypeRequests),
                    inboundRedirectSources: wrapRequests(inboundRedirectRequests)
                }).pipe(
                    map(combined => ({
                        ...combined.initial,
                        childEntities: {
                            searchTypes: combined.searchTypes,
                            pageItemTypes: combined.pageItemTypes,
                            inboundRedirectSources: combined.inboundRedirectSources
                        }
                    }))
                );
            }),
            takeUntil(this._destroy$)
        ).subscribe({
            next: (result: any) => {
                this.isLoading = false;
                this.previewResult = result.preview;

                // Store current configuration
                this._currentSite = result.site;
                this._currentTellerConnections = result.tellerConnections;
                this._currentPages = result.pages;
                this._currentMenuItems = result.menuItems;
                this._currentEmailTemplates = result.emailTemplates;
                this._currentSmsTemplates = result.smsTemplates;

                // Store child entities in Maps
                result.pages.forEach((page: PageModel) => {
                    if (page.id && result.childEntities) {
                        this._currentSearchTypes.set(page.id, result.childEntities.searchTypes[`page_${page.id}`] || []);
                        this._currentPageItemTypes.set(page.id, result.childEntities.pageItemTypes[`page_${page.id}`] || []);
                        this._currentInboundRedirectSources.set(page.id, result.childEntities.inboundRedirectSources[`page_${page.id}`] || []);
                    }
                });

                if (result.preview.success && result.preview.incomingConfiguration) {
                    // Compute the preview by comparing incoming vs current
                    this.computedPreview = this.computePreview(
                        result.preview.incomingConfiguration,
                        result.preview.assets,
                        result.preview.sourceEnvironmentName,
                        result.preview.targetEnvironmentName
                    );
                    this.currentStep = 'preview';
                } else if (result.preview.errors && result.preview.errors.length > 0) {
                    this.errorMessage = result.preview.errors.join('\n');
                    this.currentStep = 'error';
                }
            },
            error: (error) => {
                this.isLoading = false;
                this.errorMessage = error?.message || 'Failed to preview import';
                this.currentStep = 'error';
            }
        });
    }

    private computePreview(incoming: PublicPortalSiteExportDto, assets: ImportAssetInfo | undefined, sourceEnvironmentName?: string, targetEnvironmentName?: string): ComputedPreview {
        // Store raw search type image changes for later matching to search types
        this._incomingSearchTypeImageChanges = assets?.searchTypeImageChanges || [];

        // Store raw page item type image changes for later matching to page item types
        this._incomingPageItemTypeImageChanges = assets?.pageItemTypeImageChanges || [];

        const { assetChanges, searchTypeImageChanges } = this.computeAssetChanges(assets);
        return {
            sourceTenantName: incoming.tenantName,
            targetTenantName: this.data.tenantName,
            sourceEnvironmentName: sourceEnvironmentName || null,
            targetEnvironmentName: targetEnvironmentName || null,
            siteChanges: this.computeSiteChanges(incoming),
            tellerConnections: this.computeTellerConnectionChanges(incoming.tellerConnections || []),
            menuItems: this.computeMenuItemChanges(incoming.menuItems || []),
            emailTemplates: this.computeEmailTemplateChanges(incoming.emailTemplates || []),
            smsTemplates: this.computeSmsTemplateChanges(incoming.smsTemplates || []),
            assets: assets || null,
            assetChanges,
            searchTypeImageChanges
        };
    }

    private computeAssetChanges(incoming: ImportAssetInfo | undefined): { assetChanges: AssetChangeInfo[], searchTypeImageChanges: AssetChangeInfo[] } {
        const assetChanges: AssetChangeInfo[] = [];
        const searchTypeImageChanges: AssetChangeInfo[] = [];

        // Use backend-computed change info for logo, favicon, styles
        if (incoming?.logoChange && incoming.logoChange.changeType !== AssetChangeType.None) {
            assetChanges.push({
                name: 'Logo',
                hasIncoming: incoming.logoChange.hasIncoming,
                currentExists: incoming.logoChange.currentExists,
                changeType: incoming.logoChange.changeType
            });
        }

        if (incoming?.faviconChange && incoming.faviconChange.changeType !== AssetChangeType.None) {
            assetChanges.push({
                name: 'Favicon',
                hasIncoming: incoming.faviconChange.hasIncoming,
                currentExists: incoming.faviconChange.currentExists,
                changeType: incoming.faviconChange.changeType
            });
        }

        if (incoming?.stylesChange && incoming.stylesChange.changeType !== AssetChangeType.None) {
            assetChanges.push({
                name: 'Custom Styles',
                hasIncoming: incoming.stylesChange.hasIncoming,
                currentExists: incoming.stylesChange.currentExists,
                changeType: incoming.stylesChange.changeType
            });
        }

        // Search Type Images - use individual comparison results from backend (separate list)
        if (incoming?.searchTypeImageChanges && incoming.searchTypeImageChanges.length > 0) {
            for (const imageChange of incoming.searchTypeImageChanges) {
                if (imageChange.change && imageChange.change.changeType !== AssetChangeType.None) {
                    searchTypeImageChanges.push({
                        name: `${imageChange.searchTypeLabel} (${imageChange.pageHeader})`,
                        hasIncoming: imageChange.change.hasIncoming,
                        currentExists: imageChange.change.currentExists,
                        changeType: imageChange.change.changeType
                    });
                }
            }
        }

        // Note: Page Item Type Images are now handled individually and grouped with their parent
        // PageItemType entities (similar to Search Type Images), not shown in the assets section

        return { assetChanges, searchTypeImageChanges };
    }

    private computeSiteChanges(incoming: PublicPortalSiteExportDto): SitePropertyChange[] {
        const current = this._currentSite;
        const changes: SitePropertyChange[] = [];

        const addChange = (name: string, currentVal: string | undefined | null, incomingVal: string | undefined | null) => {
            const curr = currentVal || '';
            const inc = incomingVal || '';
            changes.push({
                name,
                currentValue: curr || '(empty)',
                incomingValue: inc || '(empty)',
                hasChanged: curr !== inc
            });
        };

        // Helper for HTML content fields - shows "(content)" / "(modified)" instead of raw HTML
        const addContentChange = (name: string, currentVal: string | undefined | null, incomingVal: string | undefined | null) => {
            const curr = currentVal || '';
            const inc = incomingVal || '';
            const hasChanged = curr !== inc;
            changes.push({
                name,
                currentValue: curr ? '(content)' : '(empty)',
                incomingValue: hasChanged ? (inc ? '(modified)' : '(empty)') : (curr ? '(content)' : '(empty)'),
                hasChanged
            });
        };

        addChange('Title', current?.title, incoming.title);
        addChange('Subtitle', current?.subtitle, incoming.subtitle);
        addChange('From Email', current?.fromEmailDisplay, incoming.fromEmailDisplay);
        addChange('Default Country', current?.defaultCountryCode, incoming.defaultCountryCode);
        addChange('Default State', current?.defaultStateCode, incoming.defaultStateCode);
        addContentChange('Footer Links Content', current?.footerLinksContent, incoming.footerLinksContent);
        addContentChange('Home Banner Content', current?.homeBannerContent, incoming.homeBannerContent);
        addContentChange('Email Footer HTML', current?.emailFooterHTML, incoming.emailFooterHTML);
        addContentChange('Cart Disclaimer', current?.cartDisclaimer, incoming.cartDisclaimer);
        addContentChange('Multi-Payment Disclaimer', current?.multiPaymentDisclaimer, incoming.multiPaymentDisclaimer);

        return changes;
    }

    private computeTellerConnectionChanges(
        incomingConnections: TellerConnectionExportDto[]
    ): TellerConnectionWithChanges[] {
        return incomingConnections.map(incomingConnection => {
            // Find matching current connection by ConfigNo
            const currentConnection =
                this._currentTellerConnections.find(
                    c => c.configNo === incomingConnection.configNo
                );

            // Compute property changes for this connection
            const propertyChanges = this.computePropertyChanges(
                currentConnection as any, incomingConnection as any
            );

            // Compute page changes — pages are matched by configNo
            const pagesWithChanges = this.computePageChanges(
                incomingConnection.pages || [],
                this._currentPages
            );

            const hasPageChanges = pagesWithChanges.some(
                p => p.changeType !== ChangeType.Unchanged
            );

            let changeType: ChangeType;
            if (!currentConnection) {
                changeType = ChangeType.Create;
            } else if (propertyChanges.length > 0 || hasPageChanges) {
                changeType = ChangeType.Update;
            } else {
                changeType = ChangeType.Unchanged;
            }

            return {
                data: incomingConnection,
                changeType,
                propertyChanges,
                pages: pagesWithChanges
            };
        });
    }

    private computePageChanges(incomingPages: PageExportDto[], currentPages: PageModel[]): PageWithChanges[] {
        return incomingPages.map(incomingPage => {
            // Match pages by configNo
            let currentPage: PageModel | undefined;
            currentPage = currentPages.find(p => p.configNo === incomingPage.configNo);

            // Compute property changes for this page
            const propertyChanges = this.computePropertyChanges(currentPage as any, incomingPage as any);

            // Compute child entity changes first so they can inform the page's changeType
            let searchTypes: SearchTypeWithChanges[] = [];
            let pageItemTypes: PageItemTypeWithChanges[] = [];
            let inboundRedirectSources: InboundRedirectSourceWithChanges[] = [];

            if (currentPage?.id) {
                const currentSearchTypes = this._currentSearchTypes.get(currentPage.id) || [];
                const currentPageItemTypes = this._currentPageItemTypes.get(currentPage.id) || [];
                const currentRedirects = this._currentInboundRedirectSources.get(currentPage.id) || [];

                searchTypes = this.computeSearchTypeChanges(incomingPage.searchTypes || [], currentSearchTypes, incomingPage.header);
                pageItemTypes = this.computePageItemTypeChanges(incomingPage.pageItemTypes || [], currentPageItemTypes, incomingPage.header);
                inboundRedirectSources = this.computeInboundRedirectSourceChanges(incomingPage.inboundRedirectSources || [], currentRedirects);
            } else {
                // New page - all child entities are new
                searchTypes = this.computeSearchTypeChanges(incomingPage.searchTypes || [], [], incomingPage.header);
                pageItemTypes = this.computePageItemTypeChanges(incomingPage.pageItemTypes || [], [], incomingPage.header);
                inboundRedirectSources = (incomingPage.inboundRedirectSources || []).map(irs => ({
                    data: irs,
                    changeType: ChangeType.Create,
                    propertyChanges: []
                }));
            }

            // Determine changeType after child entities are computed so child changes are reflected
            const tempPage: PageWithChanges = { data: incomingPage, changeType: ChangeType.Unchanged, propertyChanges, searchTypes, pageItemTypes, inboundRedirectSources };
            const hasChildChanges = this.getPageChildChangesCount(tempPage) > 0;
            let changeType: ChangeType;
            if (!currentPage) {
                changeType = ChangeType.Create;
            } else if (propertyChanges.length > 0 || hasChildChanges) {
                changeType = ChangeType.Update;
            } else {
                changeType = ChangeType.Unchanged;
            }

            return {
                data: incomingPage,
                changeType,
                propertyChanges,
                searchTypes,
                pageItemTypes,
                inboundRedirectSources
            };
        });
    }

    private formatValue(val: unknown): string {
        if (val === null || val === undefined) return '(empty)';
        if (typeof val === 'boolean') return val ? 'Yes' : 'No';
        if (typeof val === 'number') return val.toString();
        return String(val) || '(empty)';
    }

    private _resolveConnectionName(
        connectionId: number | undefined | null
    ): string | undefined {
        if (connectionId === undefined || connectionId === null) {
            return undefined;
        }

        const connection = this._currentTellerConnections.find(
            c => c.id === connectionId
        );

        return connection?.name
            || connection?.tellerSystemCode
            || `Connection ${connectionId}`;
    }

    private computePropertyChanges(
        current: Record<string, unknown> | undefined,
        incoming: Record<string, unknown>
    ): PropertyChange[] {
        if (!current) return [];

        const changes: PropertyChange[] = [];

        for (const key of Object.keys(incoming)) {
            if (ConfigImportWizardComponent.EXCLUDE_KEYS.has(key)) continue;

            const inc = incoming[key];

            if (Array.isArray(inc)) continue;

            const curr = current[key];
            const name = ConfigImportWizardComponent.camelCaseToLabel(key);

            if (ConfigImportWizardComponent.CONTENT_KEYS.has(key)) {
                if ((curr || null) !== (inc || null)) {
                    changes.push({ name, currentValue: '(content)', incomingValue: '(modified)' });
                }
            } else if (typeof inc === 'boolean' || typeof curr === 'boolean') {
                if (Boolean(curr) !== Boolean(inc)) {
                    changes.push({ name, currentValue: this.formatValue(curr), incomingValue: this.formatValue(inc) });
                }
            } else {
                const currF = this.formatValue(curr);
                const incF = this.formatValue(inc);

                if (currF !== incF) {
                    changes.push({ name, currentValue: currF, incomingValue: incF });
                }
            }
        }

        return changes;
    }

    private countNonEmptyFields(data: Record<string, unknown>): number {
        return Object.keys(data)
            .filter(key =>
                !ConfigImportWizardComponent.EXCLUDE_KEYS.has(key) &&
                !Array.isArray(data[key]) &&
                this.formatValue(data[key]) !== '(empty)'
            ).length;
    }

    private computeMenuItemChanges(incomingMenuItems: MenuItemExportDto[]): EntityWithChange<MenuItemExportDto>[] {
        return incomingMenuItems.map(incoming => {
            const current = this._currentMenuItems.find(m => m.configNo === incoming.configNo);
            const propertyChanges = this.computePropertyChanges(current as any, incoming as any);
            const changeType = current
                ? (propertyChanges.length > 0 ? ChangeType.Update : ChangeType.Unchanged)
                : ChangeType.Create;

            return { data: incoming, changeType, propertyChanges };
        });
    }

    private computeEmailTemplateChanges(incomingTemplates: EmailTemplateExportDto[]): EntityWithChange<EmailTemplateExportDto>[] {
        return incomingTemplates.map(incoming => {
            const current = this._currentEmailTemplates.find(t => t.name === incoming.name);
            const propertyChanges = this.computePropertyChanges(current as any, incoming as any);
            const changeType = current
                ? (propertyChanges.length > 0 ? ChangeType.Update : ChangeType.Unchanged)
                : ChangeType.Create;

            return { data: incoming, changeType, propertyChanges };
        });
    }

    private computeSmsTemplateChanges(incomingTemplates: SMSTemplateExportDto[]): EntityWithChange<SMSTemplateExportDto>[] {
        return incomingTemplates.map(incoming => {
            const current = this._currentSmsTemplates.find(t => t.name === incoming.name);
            const propertyChanges = this.computePropertyChanges(current as any, incoming as any);
            const changeType = current
                ? (propertyChanges.length > 0 ? ChangeType.Update : ChangeType.Unchanged)
                : ChangeType.Create;

            return { data: incoming, changeType, propertyChanges };
        });
    }

    // Child entity comparison methods

    private computeSearchTypeChanges(
        incomingSearchTypes: SearchTypeExportDto[],
        currentSearchTypes: SearchTypeModel[],
        pageHeader: string
    ): SearchTypeWithChanges[] {
        return incomingSearchTypes.map(incoming => {
            let currentSearchType: SearchTypeModel | undefined;
            currentSearchType = currentSearchTypes.find(st => st.configNo === incoming.configNo);

            const propertyChanges = this.computePropertyChanges(currentSearchType as any, incoming as any);

            // Find matching instruction image by label and page header
            const imageChange = this._incomingSearchTypeImageChanges.find(
                img => img.searchTypeLabel === incoming.label && img.pageHeader === pageHeader
            );
            const hasImageChange = imageChange?.change &&
                (imageChange.change.changeType === AssetChangeType.New || imageChange.change.changeType === AssetChangeType.Replace);

            const changeType = currentSearchType
                ? (propertyChanges.length > 0 || hasImageChange)
                    ? ChangeType.Update
                    : ChangeType.Unchanged
                : ChangeType.Create;

            return {
                data: incoming,
                changeType,
                propertyChanges,
                instructionImage: imageChange?.change
            };
        });
    }

    private computePageItemTypeChanges(
        incomingPageItemTypes: PageItemTypeExportDto[],
        currentPageItemTypes: PageItemTypeModel[],
        pageHeader: string
    ): PageItemTypeWithChanges[] {
        return incomingPageItemTypes.map(incoming => {
            let current: PageItemTypeModel | undefined;
            current = currentPageItemTypes.find(pit => pit.configNo === incoming.configNo);

            const propertyChanges = this.computePropertyChanges(current as any, incoming as any);

            // Find matching image by label and page header
            const imageChange = this._incomingPageItemTypeImageChanges.find(
                img => img.pageItemTypeLabel === incoming.label && img.pageHeader === pageHeader
            );
            const hasImageChange = imageChange?.change &&
                (imageChange.change.changeType === AssetChangeType.New || imageChange.change.changeType === AssetChangeType.Replace);

            const changeType = current
                ? (propertyChanges.length > 0 || hasImageChange)
                    ? ChangeType.Update
                    : ChangeType.Unchanged
                : ChangeType.Create;

            return {
                data: incoming,
                changeType,
                propertyChanges,
                image: imageChange?.change
            };
        });
    }

    private computeInboundRedirectSourceChanges(
        incomingSources: InboundRedirectSourceExportDto[],
        currentSources: InboundRedirectSourceModel[]
    ): InboundRedirectSourceWithChanges[] {
        return incomingSources.map(incoming => {
            let current: InboundRedirectSourceModel | undefined;
            current = currentSources.find(irs => irs.configNo === incoming.configNo);

            const propertyChanges = this.computePropertyChanges(current as any, incoming as any);
            const changeType = current
                ? (propertyChanges.length > 0 ? ChangeType.Update : ChangeType.Unchanged)
                : ChangeType.Create;

            return { data: incoming, changeType, propertyChanges };
        });
    }

    public onConfirmImport() {
        if (!this.selectedFile) return;

        this.currentStep = 'importing';
        this.isLoading = true;

        this.configurationService.importConfiguration(this.data.siteId, this.selectedFile)
            .pipe(takeUntil(this._destroy$))
            .subscribe({
                next: (result) => {
                    this.isLoading = false;
                    this.importResult = result;

                    if (result.success) {
                        this.currentStep = 'complete';
                    } else {
                        this.errorMessage = result.errors?.join('\n') || 'Import failed';
                        this.currentStep = 'error';
                    }
                },
                error: (error) => {
                    this.isLoading = false;
                    this.errorMessage = error?.message || 'Import failed';
                    this.currentStep = 'error';
                }
            });
    }

    public onBack() {
        if (this.currentStep === 'preview') {
            this.currentStep = 'file-select';
            this.previewResult = null;
            this.computedPreview = null;
        } else if (this.currentStep === 'error') {
            this.currentStep = 'file-select';
            this.errorMessage = null;
        }
    }

    public onComplete() {
        this.dialogRef.close({ imported: true });
    }

    public get totalChanges(): number {
        if (!this.computedPreview) return 0;
        let count = 0;

        // Count site property changes
        count += this.computedPreview.siteChanges.filter(c => c.hasChanged).length;

        // Count teller connection changes (entity-level and property-level)
        for (const connection of this.computedPreview.tellerConnections) {
            if (connection.changeType === ChangeType.Create) {
                count += this.getTellerConnectionNewFieldCount(connection.data);
            } else if (connection.changeType === ChangeType.Update) {
                count += connection.propertyChanges?.length || 0;
            }

            // Count page changes (entity-level and property-level)
            for (const page of connection.pages) {
                if (page.changeType === ChangeType.Create) {
                    count += this.getPageNewFieldCount(page.data);
                } else if (page.changeType === ChangeType.Update) {
                    count += page.propertyChanges?.length || 0; // Count individual property changes
                }

                // Count child entity changes
                count += this.getPageChildChangesCount(page);
            }
        }

        // Count other entity changes (entity-level and property-level)
        for (const menuItem of this.computedPreview.menuItems) {
            if (menuItem.changeType === ChangeType.Create) {
                count += this.getMenuItemNewFieldCount(menuItem.data);
            } else if (menuItem.changeType === ChangeType.Update) {
                count += menuItem.propertyChanges?.length || 0;
            }
        }

        for (const emailTemplate of this.computedPreview.emailTemplates) {
            if (emailTemplate.changeType === ChangeType.Create) {
                count += this.getEmailTemplateNewFieldCount(emailTemplate.data);
            } else if (emailTemplate.changeType === ChangeType.Update) {
                count += emailTemplate.propertyChanges?.length || 0;
            }
        }

        for (const smsTemplate of this.computedPreview.smsTemplates) {
            if (smsTemplate.changeType === ChangeType.Create) {
                count += this.getSmsTemplateNewFieldCount(smsTemplate.data);
            } else if (smsTemplate.changeType === ChangeType.Update) {
                count += smsTemplate.propertyChanges?.length || 0;
            }
        }

        // Count asset changes (only those that are actually changing)
        // Note: Page Item Type Images and Search Type Images are now counted as part of child entity changes in getPageChildChangesCount
        for (const asset of this.computedPreview.assetChanges) {
            if (asset.changeType === AssetChangeType.New || asset.changeType === AssetChangeType.Replace) {
                count++; // Logo, Favicon, Styles count as 1 each
            }
        }

        return count;
    }

    public get hasNoChanges(): boolean {
        return this.totalChanges === 0;
    }

    public get hasAssetChanges(): boolean {
        if (!this.computedPreview?.assetChanges) return false;
        // Only count assets that are actually changing (not unchanged)
        return this.computedPreview.assetChanges.some(a => a.changeType === AssetChangeType.New || a.changeType === AssetChangeType.Replace);
    }

    public getChangeTypeLabel(changeType: ChangeType): string {
        switch (changeType) {
            case ChangeType.Create: return 'New';
            case ChangeType.Update: return 'Update';
            default: return 'No changes';
        }
    }

    public getChangeTypeClass(changeType: ChangeType): string {
        switch (changeType) {
            case ChangeType.Create: return 'change-create';
            case ChangeType.Update: return 'change-update';
            default: return 'change-unchanged';
        }
    }

    public getTotalPageCount(): number {
        if (!this.computedPreview) return 0;
        return this.computedPreview.tellerConnections.reduce(
            (total, connection) => total + (connection.pages?.length || 0), 0
        );
    }

    public getPageCount(
        connection: TellerConnectionWithChanges
    ): number {
        return connection.pages?.length || 0;
    }

    public getPageChildCount(page: PageExportDto): number {
        return (page.searchTypes?.length || 0) +
               (page.pageItemTypes?.length || 0) +
               (page.inboundRedirectSources?.length || 0);
    }

    public hasPageChildEntities(page: PageWithChanges): boolean {
        return (page.searchTypes?.length || 0) > 0 ||
               (page.pageItemTypes?.length || 0) > 0 ||
               (page.inboundRedirectSources?.length || 0) > 0;
    }

    public getPageChildChangesCount(page: PageWithChanges): number {
        let count = 0;

        page.searchTypes?.forEach(st => {
            if (st.changeType === ChangeType.Create) {
                count += this.getSearchTypeNewFieldCount(st.data);
            } else if (st.changeType === ChangeType.Update) {
                count += st.propertyChanges?.length || 0;
            }

            // Count instruction image changes
            if (st.instructionImage && (st.instructionImage.changeType === AssetChangeType.New || st.instructionImage.changeType === AssetChangeType.Replace)) {
                count++;
            }
        });

        page.pageItemTypes?.forEach(pit => {
            if (pit.changeType === ChangeType.Create) {
                count += this.getPageItemTypeNewFieldCount(pit.data);
            } else if (pit.changeType === ChangeType.Update) {
                count += pit.propertyChanges?.length || 0;
            }

            // Count image changes
            if (pit.image && (pit.image.changeType === AssetChangeType.New || pit.image.changeType === AssetChangeType.Replace)) {
                count++;
            }
        });

        page.inboundRedirectSources?.forEach(irs => {
            if (irs.changeType === ChangeType.Create) {
                count += this.getInboundRedirectSourceNewFieldCount(irs.data);
            } else if (irs.changeType === ChangeType.Update) {
                count += irs.propertyChanges?.length || 0;
            }
        });

        return count;
    }

    public getTellerConnectionNewFieldCount(data: TellerConnectionExportDto): number {
        return this.countNonEmptyFields(data as any);
    }

    public getPageNewFieldCount(data: PageExportDto): number {
        return this.countNonEmptyFields(data as any);
    }

    public getMenuItemNewFieldCount(data: MenuItemExportDto): number {
        return this.countNonEmptyFields(data as any);
    }

    public getSearchTypeNewFieldCount(data: SearchTypeExportDto): number {
        return this.countNonEmptyFields(data as any);
    }

    public getPageItemTypeNewFieldCount(data: PageItemTypeExportDto): number {
        return this.countNonEmptyFields(data as any);
    }

    public getInboundRedirectSourceNewFieldCount(data: InboundRedirectSourceExportDto): number {
        return this.countNonEmptyFields(data as any);
    }

    public getEmailTemplateNewFieldCount(data: EmailTemplateExportDto): number {
        return this.countNonEmptyFields(data as any);
    }

    public getSmsTemplateNewFieldCount(data: SMSTemplateExportDto): number {
        return this.countNonEmptyFields(data as any);
    }

    private static camelCaseToLabel(key: string): string {
        const ACRONYMS = new Set(['url', 'html']);
        const words = key
            .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
            .replace(/([a-z])([A-Z])/g, '$1 $2')
            .split(' ');

        return words
            .map(w => {
                const lower = w.toLowerCase();
                return ACRONYMS.has(lower)
                    ? w.toUpperCase()
                    : (w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
            })
            .join(' ');
    }

    public getSmsTemplateName(templateId: number): string {
        const names: Record<number, string> = {
            0: 'Payment Successful',
            1: 'Forgot Password',
            2: 'Reset Password',
            3: 'Deferred Item Notification',
            4: 'Payment Due Notification',
            5: 'Verify Phone'
        };
        return names[templateId] || `Template #${templateId}`;
    }

    public getEmailTemplateName(templateId: number): string {
        const names: Record<number, string> = {
            1: 'Payment Successful',
            2: 'Forgot Password',
            3: 'Reset Password',
            4: 'Deferred Item Notification',
            5: 'Payment Due Notification',
            6: 'Sign Up',
            7: 'Sign Up for Confirmed User'
        };
        return names[templateId] || `Template #${templateId}`;
    }
}
